import { events } from "@constants/tracking";
import { urls } from "@constants/urls";
import { useThemeContext } from "@contexts/ThemeContext";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { formatDateToLocalizedString } from "@helpers/date";
import { buildType, versionCode } from "@helpers/expo-extras";
import i18n from "@helpers/lib/i18n";
import { isWeb } from "@helpers/platform";
import { getPurchasedProducts } from "@helpers/user";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import useAuthUser from "@hooks/useAuthUser";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import useExams from "@hooks/useExams";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { composeMessage } from "@memorang/helpers/email";
import { getPlatform } from "@memorang/helpers/platform";
import { useRouter } from "expo-router";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
/* -----------------Third parties--------------- */
import { Linking } from "react-native";
/* -----------------Helpers && Hooks--------------- */
import type { Section } from "../types";

const isDevelopment = process.env.NODE_ENV === "development";

export type DialogType =
	| "EXAMS_FILTER"
	| "DATE_PICKER"
	| "SIGN_OUT"
	| "LANGUAGE"
	| "PURCHASE"
	| "FAQS"
	| "FEEDBACK"
	| "APP_SELECTION";

type Callbacks = {
	toggleDialog: (type: DialogType) => void;
	toggleDarkMode: () => void;
	toggleShowDeleteFeedbackDialog: () => void;
	toggleAudioPlaybackTestDialog: () => void;
};

const useAccountSettings = ({
	toggleDialog,
	toggleDarkMode,
	toggleShowDeleteFeedbackDialog,
	toggleAudioPlaybackTestDialog,
}: Callbacks) => {
	const router = useRouter();
	const { t } = useTranslation();
	const { handleFeedbackNavigation } = useCustomNavigation();

	const currentAppVersion =
		useAppStore((state) => state.app.version) || "0.0.0";
	const {
		currentExamContext: { examDate, examName, examIcon },
	} = useExamContext();

	const { email, isAdmin } = useAuthUser();

	const showPaywall = useGlobalStore((state) => state.showPaywall);
	const setShowPaywall = useGlobalStore((state) => state.setShowPaywall);

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const { newUserInfo } = useAuthContext();

	const username = newUserInfo?.name;

	const { isDarkMode } = useThemeContext();

	const exam = useAppStore((store) => store.exam);
	const supportEmail = useAppStore((store) => store.supportEmail);
	const aiToolsTosLink = useAppStore((store) => store.app.links.aiToolsTos);

	const privacyLink = useAppStore((store) => store.app.links.privacy);
	const showLanguageSettings = useAppStore(
		(store) => store.app.accountSettings?.showLanguageSettings,
	);
	const showPurchasedProducts = useAppStore(
		(store) => store.app.accountSettings?.showPurchasedProducts,
	);

	const showFaqs = useAppStore((store) => store.app.accountSettings?.showFaqs);

	const showPartnersSection = useAppStore(
		(store) => store.app.accountSettings?.showPartnersSection,
	);

	const showAudioTestDialog = useAppStore(
		(store) => store.app.accountSettings?.showAudioTestDialog,
	);

	const allowChangeExam = useAppStore((store) => store.exam.allowChangeExam);
	const tosLink = useAppStore((store) => store.app.links.tos);
	const { examsList } = useExams();

	const purchasedProducts = getPurchasedProducts(examsList);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const getAccountSections = (): Section[] => {
		const currentLanguage = (() => {
			switch (i18n.language) {
				case "en-US":
					return t("language.english");
				case "zh-CN":
					return t("language.chinese");
				case "ja-JP":
					return t("language.japanese");
				default:
					return t("language.english");
			}
		})();

		return [
			...(exam?.enabled
				? [
						...(allowChangeExam
							? [
									{
										sectionTitle: t("account.currentExam"),
										items: [
											{
												icon: examIcon || "file-document-outline",
												type: "exam-filter",
												title: examName,
												iconRight: "swap-horizontal",
											},
										],
									},
								]
							: []),
						...(purchasedProducts.length > 0 && showPurchasedProducts
							? [
									{
										sectionTitle: t("account.currentExam"),
										items: purchasedProducts.map(
											({
												bundle: {
													name,
													icon: { url },
												},
												metadata,
											}) => {
												return {
													icon: url,
													type: "extend-subscription",
													title: name,
													description: `${t("account.validUntil", {
														date: DateTime.fromISO(
															metadata.subscription.endDate,
														).toLocaleString(DateTime.DATE_FULL),
													})}`,
													onPress: isWeb
														? () => toggleDialog("PURCHASE")
														: () => {
																router.push({
																	pathname: "/purchase",
																	params: {
																		productType: "STUDY_PACK",
																	},
																});
															},
												};
											},
										),
									},
								]
							: []),
						{
							sectionTitle: t("account.examDate"),
							items: [
								{
									icon: "calendar-outline",
									type: "datepicker",
									title: examDate
										? formatDateToLocalizedString(examDate)
										: t("account.selectDate"),
									onPress: () => toggleDialog("DATE_PICKER"),
									iconRight: "pencil-outline",
								},
							],
						},

						...(showAudioTestDialog || isAdmin
							? [
									{
										sectionTitle: t("account.features"),
										items: [
											...(showAudioTestDialog
												? [
														{
															icon: "volume-high",
															title: t("account.audioPlaybackTest"),
															type: "toggle-audio-playback",
															onPress: () => toggleAudioPlaybackTestDialog(),
														},
													]
												: []),
											...(isAdmin
												? [
														{
															icon: showPaywall
																? "lock-open-outline"
																: "lock-outline",
															title: showPaywall
																? "Hide paywall"
																: "Show paywall",
															type: "toggle-paywall",
															onPress: () => setShowPaywall(!showPaywall),
															description: showPaywall
																? "Paywall is enabled"
																: "Paywall is disabled",
														},
													]
												: []),
										],
									},
								]
							: []),
					]
				: []),
			{
				sectionTitle: t("account.appearance"),
				items: [
					{
						icon: isDarkMode ? "weather-sunny" : "weather-night",
						title: t("account.darkMode"),
						type: "toggle",
						onPress: () => toggleDarkMode(),
					},
				],
			},
			{
				sectionTitle: t("account.accountInfo"),
				items: [
					{
						icon: "account-outline",
						title: t("account.name"),
						description: username || "",
					},
					{
						icon: "email-outline",
						title: t("account.email"),
						description: email,
					},
				],
			},
			...(showLanguageSettings
				? [
						{
							sectionTitle: t("language.display"),
							items: [
								{
									icon: "translate",
									title: t("language.sectionTitle"),
									onPress: () => toggleDialog("LANGUAGE"),
									description: t("language.currentLanguage", {
										langname: currentLanguage,
									}),
								},
							],
						},
					]
				: []),
			...(isDevelopment
				? [
						{
							sectionTitle: t("account.dev"),
							items: [
								{
									onPress: () => {
										router.push("/sandbox");
									},
									icon: "code-json",
									title: t("account.sandbox"),
								},
								{
									onPress: () => toggleDialog("APP_SELECTION"),
									icon: "apps",
									title: "Switch app",
									description: `Current app: ${buildType}`,
								},
							],
						},
					]
				: []),
			{
				sectionTitle: t("account.about"),
				items: [
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.termsOfServiceClicked,
							});
							Linking.openURL(tosLink);
						},
						icon: "gavel",
						title: t("account.termsOfService"),
						iconRight: "open-in-new",
					},
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.privacyPolicyClicked,
							});
							Linking.openURL(privacyLink);
						},
						icon: "eye-outline",
						title: t("account.privacyPolicy"),
						iconRight: "open-in-new",
					},
					...(aiToolsTosLink
						? [
								{
									onPress: () => {
										explicitCallTrackCustomEvent({
											eventName: events.aiToolsTosClicked,
										});
										Linking.openURL(aiToolsTosLink);
									},
									icon: "robot-outline",
									title: t("account.aiToolsTos"),
									iconRight: "open-in-new",
								},
							]
						: []),
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.contactSupportClicked,
								username,
								email,
							});
							const platform = getPlatform();
							const subject = `${buildType} ${t("account.support")} | ${platform} | ${username}`;
							const body = t("account.supportEmailBody", { username, email });
							composeMessage(subject, body, supportEmail);
						},
						icon: "help-circle-outline",
						title: t("account.contactSupport"),
						iconRight: "open-in-new",
					},
					{
						onPress: () =>
							handleFeedbackNavigation(() => toggleDialog("FEEDBACK")),
						icon: "message-text-outline",
						title: t("common.leaveFeedback"),
					},
					...(showFaqs
						? [
								{
									onPress: isWeb
										? () => toggleDialog("FAQS")
										: () => {
												router.push("/faqs");
											},
									icon: "message-question-outline",
									title: t("account.frequentlyAskedQuestions"),
								},
							]
						: []),
					{
						onPress: () => toggleDialog("SIGN_OUT"),
						icon: "logout",
						title: t("account.signOut"),
					},
					{
						onPress: toggleShowDeleteFeedbackDialog,
						icon: "account-remove-outline",
						title: t("account.deleteAccount"),
					},
				],
			},
			{
				sectionTitle: t("account.platform"),
				items: [
					...(showPartnersSection
						? [
								{
									onPress: () => {
										Linking.openURL(urls.core);
									},
									icon: "web",
									title: t("account.partnerships"),
									iconRight: "open-in-new",
								},
							]
						: []),
					{
						icon: "unfold-more-vertical",
						title: `${currentAppVersion}${versionCode ? ` (${versionCode})` : ""}`,
					},
				],
			},
		];
	};
	return {
		isLoading: false,
		getAccountSections,
		usernameValue: username,
		actualUsernameValue: username,
	};
};

export default useAccountSettings;
